import React, { useState } from 'react';
import { FaTimes, FaSync } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ServiceContractService, { IntervalUnit, AmountMode, CreateServiceContractPayload } from '../services/ServiceContractService';
import NumericInput from '../../../components/NumericInput';
import { parseLocaleNumber } from '../../../utils/numberFormatting';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

interface CreateServiceContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  offerId: number;
  offerTitle: string;
  customerName: string;
  offerTotalGross: number;
  onCreated: () => void;
}

export default function CreateServiceContractModal({
  isOpen,
  onClose,
  offerId,
  offerTitle,
  customerName,
  offerTotalGross,
  onCreated,
}: CreateServiceContractModalProps): React.JSX.Element | null {
  const todayISO = new Date().toISOString().split('T')[0];

  const [name, setName] = useState(offerTitle || `Service contract – ${customerName}`);
  const [description, setDescription] = useState('');
  const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('MONTH');
  const [intervalCount, setIntervalCount] = useState(1);
  const [startDate, setStartDate] = useState(todayISO);
  const [isOpenEnded, setIsOpenEnded] = useState(true);
  const [endDate, setEndDate] = useState('');
  const [minTermMonths, setMinTermMonths] = useState<number | null>(12);
  const [noticePeriodDays, setNoticePeriodDays] = useState<number | null>(30);
  const [amountMode, setAmountMode] = useState<AmountMode>('FROM_OFFER_TOTAL');
  const [customAmountGross, setCustomAmountGross] = useState('');
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a contract name');
      return;
    }

    setSaving(true);
    try {
      const payload: CreateServiceContractPayload = {
        name: name.trim(),
        description: description.trim() || undefined,
        interval_unit: intervalUnit,
        interval_count: intervalCount,
        start_date: startDate,
        end_date: isOpenEnded ? null : endDate || null,
        is_open_ended: isOpenEnded,
        min_term_months: minTermMonths,
        notice_period_days: noticePeriodDays,
        amount_mode: amountMode,
        amount_gross: amountMode === 'MANUAL' ? parseLocaleNumber(customAmountGross) : undefined,
      };

      await ServiceContractService.createFromOffer(offerId, payload);
      toast.success('Service contract created');
      onCreated();
      onClose();
    } catch (err) {
      console.error('Failed to create service contract:', err);
      toast.error('Failed to create service contract');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: colors.bgCard,
          borderRadius: '12px',
          width: '600px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: shadows.elevated,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FaSync size={20} color={colors.navy} />
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.navy }}>
              Create Service Contract
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: colors.textMuted,
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Name */}
            <div>
              <label style={labelStyle}>Contract Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Monthly maintenance"
                style={inputStyle}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle}>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>

            {/* Interval */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Billing Interval</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    min={1}
                    value={intervalCount}
                    onChange={(e) => setIntervalCount(parseInt(e.target.value, 10) || 1)}
                    style={{ ...inputStyle, width: '70px' }}
                  />
                  <select
                    value={intervalUnit}
                    onChange={(e) => setIntervalUnit(e.target.value as IntervalUnit)}
                    style={inputStyle}
                  >
                    <option value="MONTH">Month(s)</option>
                    <option value="QUARTER">Quarter(s)</option>
                    <option value="YEAR">Year(s)</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={inputStyle}
                  required
                />
              </div>
            </div>

            {/* Open-ended / End date */}
            <div>
              <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={isOpenEnded}
                  onChange={(e) => setIsOpenEnded(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                Open-ended contract (no fixed end date)
              </label>
              {!isOpenEnded && (
                <div style={{ marginTop: '12px' }}>
                  <label style={labelStyle}>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            {/* Min term & Notice period */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={labelStyle}>Minimum Term (months)</label>
                <input
                  type="number"
                  min={0}
                  value={minTermMonths ?? ''}
                  onChange={(e) => setMinTermMonths(e.target.value ? parseInt(e.target.value, 10) : null)}
                  placeholder="e.g. 12"
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Notice Period (days)</label>
                <input
                  type="number"
                  min={0}
                  value={noticePeriodDays ?? ''}
                  onChange={(e) => setNoticePeriodDays(e.target.value ? parseInt(e.target.value, 10) : null)}
                  placeholder="e.g. 30"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* Amount */}
            <div>
              <label style={labelStyle}>Recurring Amount</label>
              <select
                value={amountMode}
                onChange={(e) => setAmountMode(e.target.value as AmountMode)}
                style={inputStyle}
              >
                <option value="FROM_OFFER_TOTAL">Use offer total ({formatCurrency(offerTotalGross)})</option>
                <option value="MANUAL">Custom amount</option>
              </select>
              {amountMode === 'MANUAL' && (
                <div style={{ marginTop: '12px' }}>
                  <label style={labelStyle}>Custom Amount (gross)</label>
                  <NumericInput
                    value={customAmountGross}
                    onChange={setCustomAmountGross}
                    placeholder="0,00"
                    style={inputStyle}
                  />
                </div>
              )}
            </div>

            {/* Info box */}
            <div
              style={{
                padding: '12px 16px',
                background: colors.infoLight,
                borderRadius: '8px',
                fontSize: '13px',
                color: colors.info,
              }}
            >
              The service contract will be linked to this offer and the customer. Recurring invoices can be
              generated based on the billing interval.
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              padding: '16px 24px',
              borderTop: `1px solid ${colors.border}`,
              background: colors.bgMuted,
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                ...ps.buttonSecondary,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                ...(saving ? ps.buttonDisabled : ps.buttonPrimary),
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Creating...' : 'Create Contract'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  ...ps.labelStyle,
};

const inputStyle: React.CSSProperties = {
  ...ps.inputStyle,
  boxSizing: 'border-box',
};
